/**
 * Application Router Component
 * 
 * Main routing component that handles all navigation within the application.
 * Manages public and protected routes, authentication checks, and redirects.
 * Uses React Router for client-side routing and implements route protection.
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../hooks/redux';
import { useAuthInit } from '../hooks/useAuthInit';
import { useRBAC } from '../hooks/useRBAC';
import LoginContainer from '../containers/LoginContainer';
import SelfAppraisalContainer from '../containers/SelfAppraisalContainer';
import LayoutContainer from '../containers/LayoutContainer';
import ProfilePage from '../pages/features/profile/ProfilePage.tsx';
import ErrorPage from '../pages/errors/ErrorPage.tsx';
import ProtectedRoute from './ProtectedRoute.tsx';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.tsx';
import TermsConditions from '../pages/documents/TermsConditions.tsx';
import PrivacyPolicy from '../pages/documents/PrivacyPolicy.tsx';
import NominationReviewPage from '../pages/features/nomination_review/NominationReviewPage.tsx';
import AppraisalAdminPage from '../pages/features/appraisal_admin/AppraisalAdminPage.tsx';
import AppraisalAdminViewDetails from '../pages/features/appraisal_admin/AppraisalAdminViewDetails.tsx';
import NominationApprovalPageView from '../pages/features/nomination_approval/NominationApprovalPageView.tsx';
import NominationApprovalDetailPage from '../pages/features/nomination_approval/NominationApprovalDetailPage.tsx';
import TeamReviewPageView from '../pages/features/team_review/TeamReviewPageView.tsx';
import TeamReviewDetailPageView from '../pages/features/team_review/TeamReviewDetailPageView.tsx';
import GlobalErrorListener from '../components/system/GlobalErrorListener.tsx';

import HODReviewPageView from '../pages/features/hod_review/HODReviewPageView.tsx';
import HODReviewDetailPageView from '../pages/features/hod_review/HODReviewDetailPageView.tsx';
import FAQ from '../pages/documents/FAQ.tsx';
import ExternalLayout from '../components/external/layout/ExternalLayout';
import External360FeedbackPage from '../pages/features/external/External360FeedbackPage';
import External360FeedbackDetailPage from '../pages/features/external/External360FeedbackDetailPage';

// Defined OUTSIDE AppRouter so the component reference is stable across re-renders.
// If defined inside AppRouter, every re-render creates a new function reference which
// React Router treats as a different component type, causing unmount/remount of children.
const ProtectedRouteWithPermission: React.FC<{
  children: React.ReactElement;
  permission: 'self_appraisal' | 'self_appraisal_okr' | 'self_appraisal_nomination' | 'nomination_review' | 'nomination_approval' | 'okr_approval' | 'team_review' | 'hod_review' | 'admin' | 'approval';
  redirectTo?: string;
}> = ({ children, permission, redirectTo }) => {
  const { hasAccess, isLoading: rbacLoading } = useRBAC();

  // Wait for RBAC to load before checking permissions
  if (rbacLoading) {
    console.log(`⏳ ProtectedRouteWithPermission - RBAC loading for permission: ${permission}`);
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!hasAccess(permission)) {
    const fallbackRoute = redirectTo || '/';
    console.warn(`🚫 Access denied to route - Missing permission: ${permission}, redirecting to ${fallbackRoute}`);
    return <Navigate to={fallbackRoute} replace />;
  }
  return children;
};

/**
 * AppRouter Component
 * 
 * Configures and renders the application's routing structure.
 * Handles authentication state and provides appropriate redirects
 * based on user authentication status.
 * 
 * @returns JSX element containing the complete routing structure
 */
const AppRouterContent: React.FC = () => {
  const location = useLocation();
  const isExternalFeedbackRoute = location.pathname.startsWith('/external-360-feedback');

  // Get authentication state from Redux store
  const { isAuthenticated, isLoading, user } = useAppSelector((state) => state.auth);
  const { hasAccess, isLoading: rbacLoading } = useRBAC();
  
  // Initialize authentication from localStorage on app startup
  useAuthInit(!isExternalFeedbackRoute);

  // Helper function to get the first accessible feature route (not profile)
  const getFeatureRoute = React.useCallback(() => {
    // Check permissions in order of priority
    if (hasAccess('self_appraisal_okr')) return '/self-appraisal/okr-submission';
    if (hasAccess('self_appraisal_nomination')) return '/self-appraisal/nominations';
    if (hasAccess('nomination_review')) return '/360-feedback';
    if (hasAccess('approval')) return '/approvals';
    if (hasAccess('team_review')) return '/team-review';
    if (hasAccess('hod_review')) return '/hod-review';
    if (hasAccess('admin')) return '/appraisal-admin';
    
    // Fallback: Try self_appraisal (parent permission) as a last resort
    if (hasAccess('self_appraisal')) return '/self-appraisal/okr-submission';
    
    // If truly no permissions, log error and return to objectives as safe fallback
    console.error('🚫 User has no accessible routes after RBAC loaded! Using fallback route.');
    return '/self-appraisal/okr-submission'; // Safe fallback instead of error page
  }, [hasAccess]);

  // Helper function to get the profile route
  const getProfileRoute = React.useCallback(() => {
    if (user?.email) {
      return `/profile/${encodeURIComponent(user.email)}`;
    }
    // Fallback to first accessible feature if profile not available
    return getFeatureRoute();
  }, [user, getFeatureRoute]);

  // Helper function to get default route based on context
  const getDefaultRoute = React.useCallback((forSelfAppraisal = false) => {
    // For self-appraisal routes, always use feature-based routing
    if (forSelfAppraisal) {
      return getFeatureRoute();
    }
    
    // For initial login and root redirect, go to profile
    return getProfileRoute();
  }, [getProfileRoute, getFeatureRoute]);

  // Debug logging for authentication state changes
  React.useEffect(() => {
    console.log(`🔍 AppRouter - Auth state changed: isAuthenticated=${isAuthenticated}, isLoading=${isLoading}`);
  }, [isAuthenticated, isLoading]);

  // Smart redirect component that redirects to first available route
  const SmartRedirect: React.FC = () => {
    // Wait for RBAC to load before determining route
    if (rbacLoading) {
      console.log('⏳ SmartRedirect - RBAC loading, showing spinner');
      return (
        <div className="flex items-center justify-center min-h-screen bg-white">
          <LoadingSpinner size="lg" />
        </div>
      );
    }
    
    const defaultRoute = getDefaultRoute();
    console.log(`🔀 SmartRedirect - Redirecting to: ${defaultRoute}`);
    return <Navigate to={defaultRoute} replace />;
  };

  // Self-appraisal redirect component that redirects to OKR or Nominations based on permissions
  const SelfAppraisalRedirect: React.FC = () => {
    // Wait for RBAC to load before determining route
    if (rbacLoading) {
      console.log('⏳ SelfAppraisalRedirect - RBAC loading, showing spinner');
      return (
        <div className="flex items-center justify-center min-h-screen bg-white">
          <LoadingSpinner size="lg" />
        </div>
      );
    }
    
    const featureRoute = getDefaultRoute(true); // Pass true to get feature route
    console.log(`🔀 SelfAppraisalRedirect - Redirecting to: ${featureRoute}`);
    return <Navigate to={featureRoute} replace />;
  };

  if (isExternalFeedbackRoute) {
    return (
      <Routes>
        <Route
          path="/external-360-feedback"
          element={
            <ExternalLayout>
              <External360FeedbackPage />
            </ExternalLayout>
          }
        />
        <Route
          path="/external-360-feedback/:nomination_relationship_id/:tab"
          element={
            <ExternalLayout>
              <External360FeedbackDetailPage />
            </ExternalLayout>
          }
        />
      </Routes>
    );
  }

  // Show loading spinner while checking authentication status
  // This prevents showing error page briefly during login
  if (isLoading || rbacLoading) {
    console.log(`⏳ AppRouter - Showing loading spinner (auth: ${isLoading}, rbac: ${rbacLoading})`);
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <GlobalErrorListener />
      <Routes>
        {/* Public Routes - Accessible without authentication */}
        <Route 
          path="/login" 
          element={
            // Redirect to dashboard if already authenticated
            isAuthenticated ? (
              <>
                {console.log(`🚀 AppRouter - Authenticated user accessing /login, redirecting to ${getDefaultRoute()}`)}
                <Navigate to={getDefaultRoute()} replace />
              </>
            ) : (
              <>
                {console.log('👤 AppRouter - Unauthenticated user accessing /login, showing LoginContainer')}
                <LoginContainer />
              </>
            )
          } 
        />
        <Route
          path='/faq'
          element={<FAQ/>}
        />
        <Route
          path='/terms-conditions'
          element={<TermsConditions />}
        />
        <Route
          path='/privacy-policy'
          element={<PrivacyPolicy />}
        />
        {/* Protected Routes - Require authentication */}
        <Route 
          path="/*" 
          element={
            <ProtectedRoute>
              <LayoutContainer>
                <Routes>
                  {/* Default redirect to first accessible route */}
                  <Route path="/" element={<SmartRedirect />} />

                  {/* Main application pages */}
                  <Route path="/self-appraisal" element={<SelfAppraisalRedirect />} />
                  <Route 
                    path="/self-appraisal/okr-submission" 
                    element={
                      <ProtectedRouteWithPermission permission="self_appraisal_okr">
                        <SelfAppraisalContainer />
                      </ProtectedRouteWithPermission>
                    } 
                  />
                  <Route 
                    path="/self-appraisal/nominations" 
                    element={
                      <ProtectedRouteWithPermission permission="self_appraisal_nomination">
                        <SelfAppraisalContainer />
                      </ProtectedRouteWithPermission>
                    } 
                  />
                  <Route 
                    path="/360-feedback"
                    element={
                      <ProtectedRouteWithPermission permission="nomination_review">
                        <NominationReviewPage/>
                      </ProtectedRouteWithPermission>
                    } 
                  />
                  <Route 
                    path="/360-feedback/:employeeId/:tab"
                    element={
                      <ProtectedRouteWithPermission permission="nomination_review">
                        <NominationReviewPage/>
                      </ProtectedRouteWithPermission>
                    } 
                  />
                  <Route 
                    path="/approvals" 
                    element={
                      <ProtectedRouteWithPermission permission="approval">
                        <NominationApprovalPageView/>
                      </ProtectedRouteWithPermission>
                    } 
                  />
                  <Route 
                    path="/approvals/:id" 
                    element={
                      <ProtectedRouteWithPermission permission="approval">
                        <NominationApprovalDetailPage/>
                      </ProtectedRouteWithPermission>
                    } 
                  />
                  <Route 
                    path="/team-review" 
                    element={
                      <ProtectedRouteWithPermission permission="team_review">
                        <TeamReviewPageView/>
                      </ProtectedRouteWithPermission>
                    } 
                  />
                  <Route 
                    path="/team-review/:nominationRelationshipId" 
                    element={
                      <ProtectedRouteWithPermission permission="team_review">
                        <TeamReviewDetailPageView/>
                      </ProtectedRouteWithPermission>
                    } 
                  />
                  <Route 
                    path="/hod-review" 
                    element={
                      <ProtectedRouteWithPermission permission="hod_review">
                        <HODReviewPageView/>
                      </ProtectedRouteWithPermission>
                    } 
                  />
                  <Route 
                    path="/hod-review/:nominationRelationshipId" 
                    element={
                      <ProtectedRouteWithPermission permission="hod_review">
                        <HODReviewDetailPageView/>
                      </ProtectedRouteWithPermission>
                    } 
                  />
                  <Route 
                    path="/appraisal-admin" 
                    element={
                      <ProtectedRouteWithPermission permission="admin">
                        <AppraisalAdminPage/>
                      </ProtectedRouteWithPermission>
                    } 
                  />
                  <Route 
                    path="/appraisal-admin/:employeeId" 
                    element={
                      <ProtectedRouteWithPermission permission="admin">
                        <AppraisalAdminViewDetails/>
                      </ProtectedRouteWithPermission>
                    } 
                  />
                  {/* Profile page - accessible to all authenticated users */}
                  <Route path="/profile/:id" element={<ProfilePage />} />
                  
                  {/* FAQ page - accessible to all authenticated users */}
                  <Route path="/faq" element={<FAQ />} />
                  
                  {/* Error page - accessible without specific permissions */}
                  <Route path="/error" element={<ErrorPage />} />                  
                  
                  {/* Catch-all route for invalid paths */}
                  <Route path="*" element={<SmartRedirect />} />
                </Routes>
              </LayoutContainer>
            </ProtectedRoute>
            
          } 
        />

        
        {/* Global fallback route */}
        <Route 
          path="*" 
          element={
            isAuthenticated ? (
              <SmartRedirect />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
      </Routes>
    </>
  );
};

const AppRouter: React.FC = () => (
  <BrowserRouter>
    <AppRouterContent />
  </BrowserRouter>
);

export default AppRouter;
